import {
  KeyboardEvent,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Popover } from '@base-ui/react/popover';
import { CaretDownIcon, CaretUpIcon } from '@phosphor-icons/react';
import cn from 'classnames';

import PillButton from '_components/UI/PillButton';

import styles from './ImageTabBar.module.scss';

export interface ImageTabBarTab {
  id: string;
  label: string;
}

export interface ImageTabBarProps {
  tabs: ImageTabBarTab[];
  activeTabId: string;
  onTabChange: (tabId: string) => void;
  /** Desktop shows a row of pill buttons; mobile collapses the same choice
      into a trigger + expanded option list (Figma node 803:5624, "Image Tab
      Bar" — a dedicated component, not the app's generic `Tabs`/`DropDown`,
      since its mobile expanded list has its own bespoke look: a shaded
      `neutrals-100` panel with per-row pills, not a plain option list).
      `true` renders that collapsed dropdown — always on mobile, and on
      desktop too wherever the pill row would cover a hotspot dot (see
      `ImageHotspot`'s `preferCompactTabs`). */
  compact: boolean;
  /** Id of the (single, content-swapping) panel the desktop tabs control —
      the parent's own `id`/`role="tabpanel"` element. Completes the ARIA
      tab/tabpanel relationship: each `role="tab"` button gets a matching
      `id`/`aria-controls` pair derived from this. */
  panelId: string;
  ariaLabel?: string;
  className?: string;
}

/** Desktop tab button id derived from the shared `panelId` — referenced by
    the panel's own `aria-labelledby` for whichever tab is currently active. */
export const imageTabId = (panelId: string, tabId: string): string =>
  `${panelId}-tab-${tabId}`;

/**
 * "Image Tab Bar" (Figma 803:5624): the CIP/CLP hotspot banner's own tab
 * switcher, distinct from the app's generic `_components/UI/Tabs` (an ARIA
 * tablist) and `DropDown` (a general-purpose popover) — this component
 * matches Figma's specific anatomy and mobile expanded-list styling, while
 * still reusing `PillButton` for the individual desktop pills and Base UI's
 * `Popover` for the mobile trigger/positioning, the same primitives those
 * shared components are themselves built from.
 *
 * Desktop keeps the standard ARIA tablist keyboard pattern (roving
 * tabindex — only the active pill is a `Tab` stop; arrow keys move between
 * pills) to match `_components/UI/Tabs`' own behaviour elsewhere in the app.
 */
const ImageTabBar = ({
  tabs,
  activeTabId,
  onTabChange,
  compact,
  panelId,
  ariaLabel,
  className,
}: ImageTabBarProps): ReactElement | null => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  tabRefs.current = tabRefs.current.slice(0, tabs.length);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  optionRefs.current = optionRefs.current.slice(0, tabs.length);

  // ARIA APG's listbox pattern: when it receives focus, the selected option
  // (not necessarily the first) should be focused — Base UI's own default
  // "focus the first tabbable element" would otherwise skip every option
  // this component deliberately took out of tab order via the roving
  // tabindex below.
  useEffect(() => {
    if (!mobileOpen) return;
    const activeIndex = tabs.findIndex((tab) => tab.id === activeTabId);
    optionRefs.current[activeIndex === -1 ? 0 : activeIndex]?.focus();
  }, [mobileOpen, tabs, activeTabId]);

  if (tabs.length === 0) {
    return null;
  }

  const activeTab = tabs.find((tab) => tab.id === activeTabId) ?? tabs[0];

  const selectTab = (tabId: string) => {
    if (tabId !== activeTabId) {
      onTabChange(tabId);
    }
  };

  const handleKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number
  ) => {
    let nextIndex = -1;

    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        nextIndex = (index + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        event.preventDefault();
        nextIndex = (index - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        event.preventDefault();
        nextIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    tabRefs.current[nextIndex]?.focus();
    selectTab(tabs[nextIndex].id);
  };

  /** Roving tabindex for the mobile `listbox`/`option` pair (WAI-ARIA APG's
      listbox keyboard pattern): only the active option is a Tab stop, and
      Arrow/Home/End move focus (without selecting — a listbox lets you
      arrow through options before committing, unlike the desktop tablist's
      select-as-you-go). */
  const handleOptionKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number
  ) => {
    let nextIndex = -1;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        nextIndex = (index + 1) % tabs.length;
        break;
      case 'ArrowUp':
        event.preventDefault();
        nextIndex = (index - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        event.preventDefault();
        nextIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    optionRefs.current[nextIndex]?.focus();
  };

  if (compact) {
    return (
      <Popover.Root open={mobileOpen} onOpenChange={setMobileOpen}>
        <Popover.Trigger
          render={(triggerProps) => (
            <button
              type="button"
              {...triggerProps}
              className={cn(styles.trigger, className)}
            >
              <span className={styles.triggerLabel}>{activeTab.label}</span>
              {mobileOpen ? (
                <CaretUpIcon
                  className={styles.caret}
                  size={24}
                  aria-hidden
                  weight="regular"
                />
              ) : (
                <CaretDownIcon
                  className={styles.caret}
                  size={24}
                  aria-hidden
                  weight="regular"
                />
              )}
            </button>
          )}
        />
        <Popover.Portal>
          <Popover.Positioner
            side="bottom"
            align="start"
            sideOffset={8}
            className={styles.positioner}
          >
            <Popover.Popup>
              <div
                role="listbox"
                aria-label={ariaLabel}
                className={styles.optionList}
              >
                {tabs.map((tab, index) => {
                  const isActive = tab.id === activeTabId;
                  return (
                    <button
                      key={tab.id}
                      ref={(node) => {
                        optionRefs.current[index] = node;
                      }}
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      tabIndex={isActive ? 0 : -1}
                      className={cn(styles.option, {
                        [styles.optionSelected]: isActive,
                      })}
                      onClick={() => {
                        selectTab(tab.id);
                        setMobileOpen(false);
                      }}
                      onKeyDown={(event) => handleOptionKeyDown(event, index)}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>
    );
  }

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(styles.bar, className)}
    >
      {tabs.map((tab, index) => {
        const isActive = tab.id === activeTabId;
        return (
          <PillButton
            key={tab.id}
            ref={(node) => {
              tabRefs.current[index] = node;
            }}
            id={imageTabId(panelId, tab.id)}
            role="tab"
            aria-selected={isActive}
            aria-controls={panelId}
            tabIndex={isActive ? 0 : -1}
            active={isActive}
            size="smaller"
            label={tab.label}
            className={styles.pill}
            onClick={() => selectTab(tab.id)}
            onKeyDown={(event) => handleKeyDown(event, index)}
          />
        );
      })}
    </div>
  );
};

export default ImageTabBar;
